/**
 * TeamAnalyser — /league/:id/team-analyser (Analysers rebuild, Stage 4)
 *
 * One JSON GET, not streamed markdown the frontend used to regex-parse via
 * parseTeam() (a silent-failure surface — a single drifted heading emptied
 * the whole page with no error at all). overview.record/rank come from real
 * standings; championshipOdds/playoffOdds come from titleOddsService's Monte
 * Carlo sim and are null (never a placeholder) when the league is too thin to
 * simulate. topPerformers/tradeCandidates/dropCandidates are all code-selected
 * server-side (teamAnalyserService.js) — Claude only writes the why/reason
 * text for names it's handed, validated against the real roster, so nothing
 * here can be a fabricated player.
 *
 * Three explicit states: pre-draft (no real roster to analyse yet), loading
 * (inline spinner), error (message + retry) — this page never renders blank.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import PlayerPhoto from '../components/PlayerPhoto'
import JaiRichText from '../components/JaiRichText'
import StatTable from '../components/StatTable'
import StickyAskBar from '../components/StickyAskBar'
import UpgradePrompt from '../components/UpgradePrompt'
import TeamChip from '../components/TeamChip'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Card, StatStrip, PosChip, DarkPlanCard, solidChip, pctTone, UpdatedRow, SecondaryBtn, Notice } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'
import { addNote } from '../services/notesStore'
import { STAT_GLYPHS } from '../config/statGlyphs'
import { shareText } from '../services/share'
import { IS_NATIVE_APP } from '../services/runtime'
import { NBA_STAT_COLUMNS, NFL_QB_STAT_COLUMNS, NFL_SKILL_STAT_COLUMNS } from '../config/statColumns'

const NO_TEAM_MESSAGE = {
  'draft-incomplete': "Team Analyser needs a completed draft — there's no real roster to analyse yet.",
}

// Same "Season / Last 7 / Last 30 / Last Season" window PlayerAnalyser.jsx
// uses for its stat table — topPerformers carries the identical stat-window
// shape (buildNbaSeasonWindow/buildNflStatWindow), so a top performer's table
// reads the same as opening that player directly in Player Analyser.
const ROW_ORDER = [
  ['season', 'Season'],
  ['last7', 'Last 7 Games'],
  ['last30', 'Last 30 Games'],
  ['priorSeason', 'Last Season'],
]

function buildStatRows(stats, columns, sport) {
  if (!stats) return []
  return ROW_ORDER
    .filter(([key]) => stats[key])
    .map(([key, label]) => {
      const values = {}
      columns.forEach((c) => { values[c.key] = stats[key][c.key] })
      // NFL fantasy points is a per-game rate with no implicit window size —
      // show games played next to the label, same as Player Analyser/Opponent
      // Scout's profile modal.
      const rowLabel = sport === 'nfl' && stats[key].gamesPlayed != null
        ? `${label} (${stats[key].gamesPlayed} gp)`
        : label
      return { label: rowLabel, values }
    })
}

// ── Strengths / Weaknesses — colour-dot cards, Maintain/Improve action box ──

function AuditCard({ item, tone }) {
  const isStrength = tone === 'strength'
  const color = isStrength ? V2.success : V2.error
  const label = isStrength ? 'Strength' : 'Weakness'
  const actionLabel = isStrength ? 'Maintain' : 'Improve'
  const action = isStrength ? item.maintain : item.improve
  const actionChip = solidChip(isStrength ? V2.success : V2.warning)

  return (
    <V2Card pad="16px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 14.5, fontWeight: 700, color: V2.navy }}>{item.title}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color, fontFamily: JAI_FONTS.mono, flexShrink: 0 }}>
          {label}
        </span>
      </div>
      {item.detail && <JaiRichText content={item.detail} style={{ fontSize: 14 }} />}
      {action && (
        <div style={{ marginTop: 10, padding: '9px 13px', borderRadius: 10, background: actionChip.background, display: 'flex', alignItems: 'baseline', gap: 7 }}>
          <span style={{ fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 10.5, color: actionChip.color, flexShrink: 0 }}>{actionLabel}</span>
          <JaiRichText content={action} style={{ fontSize: 14, color: actionChip.color }} />
        </div>
      )}
    </V2Card>
  )
}

// ── Top performer — identity + stat table + why ─────────────────────────────

function TopPerformerCard({ player, sport }) {
  const columns = sport === 'nfl'
    ? (player.position === 'QB' ? NFL_QB_STAT_COLUMNS : NFL_SKILL_STAT_COLUMNS)
    : NBA_STAT_COLUMNS
  const rows = buildStatRows(player.stats, columns, sport)

  return (
    <V2Card pad="18px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: rows.length > 0 ? 12 : 10 }}>
        <PlayerPhoto playerId={player.playerId} name={player.name} size={40} rounded="full" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: V2.navy }}>{player.name}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
            <PosChip>{player.position}</PosChip>
            <TeamChip abbr={player.team} sport={sport} />
          </div>
        </div>
      </div>
      {rows.length > 0 && <StatTable title="Key numbers" columns={columns} rows={rows} />}
      {player.why && <JaiRichText content={player.why} style={{ fontSize: 13.5, marginTop: rows.length > 0 ? 0 : 4 }} />}
    </V2Card>
  )
}

// ── Trade / drop candidate row — photo, chips, reason, action link ──────────

function CandidateRow({ player, leagueId, kind, sport, isLast }) {
  const injured = player.injuryStatus && player.injuryStatus !== 'ACTIVE'
  const to = kind === 'trade'
    ? `/league/${leagueId}/trade/analyse?give=${encodeURIComponent(player.name)}`
    : `/league/${leagueId}/waiver?drop=${encodeURIComponent(player.name)}`
  const linkLabel = kind === 'trade' ? 'Trade this player →' : 'Drop this player →'

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 13, padding: '14px 20px',
      borderBottom: isLast ? 'none' : '1px solid rgba(10,31,68,0.06)', flexWrap: 'wrap',
    }}>
      <PlayerPhoto playerId={player.playerId} name={player.name} size={36} rounded="full" />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: V2.navy }}>
            {player.name}
            {injured && <span style={{ marginLeft: 6 }}>{STAT_GLYPHS.injury}</span>}
          </span>
          <PosChip>{player.position}</PosChip>
          <TeamChip abbr={player.team} sport={sport} />
        </div>
        {player.reason && <JaiRichText content={player.reason} style={{ fontSize: 13, marginTop: 5 }} />}
      </div>
      <Link to={to} style={{ fontSize: 12.5, fontWeight: 700, color: V2.blue, textDecoration: 'none', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>
        {linkLabel}
      </Link>
    </div>
  )
}

function CandidateList({ title, players, leagueId, kind, sport }) {
  if (!players?.length) return null
  return (
    <V2Card pad="0" style={{ marginBottom: 16 }}>
      <div style={{ padding: '13px 20px', borderBottom: '1px solid rgba(10,31,68,0.07)', fontSize: 13.5, fontWeight: 800, color: V2.navy, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {title}
      </div>
      {players.map((p, i) => (
        <CandidateRow key={p.playerId ?? p.name} player={p} leagueId={leagueId} kind={kind} sport={sport} isLast={i === players.length - 1} />
      ))}
    </V2Card>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────

export default function TeamAnalyser() {
  const { id } = useParams()

  const [league, setLeague] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [upgrade, setUpgrade] = useState(null)
  const [noteSaved, setNoteSaved] = useState(false)
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteError, setNoteError] = useState('')

  const sport = (league?.sport || 'nba').toLowerCase()

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true)
    setError('')
    setUpgrade(null)
    try {
      const res = await leaguesApi.analyseTeam(id, { refresh })
      setData(res.data)
      setNoteSaved(false)
    } catch (err) {
      if (err.response?.data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setUpgrade({ tool: err.response.data.requiredTool, message: err.response.data.error })
      } else {
        setError(err.response?.data?.error || "JAI couldn't analyse your team right now. Try again in a minute.")
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    leaguesApi.get(id).then((r) => { if (!cancelled) setLeague(r.data.league) }).catch(() => {})
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const saveToNotes = async () => {
    if (noteSaved || noteSaving || !data?.thoughts) return
    setNoteSaving(true)
    setNoteError('')
    try {
      await addNote(id, data.thoughts, {
        source: 'team-analyser', pinned: true,
        title: `Team Analyser — ${league?.teamName || 'your team'}`,
      })
      setNoteSaved(true)
    } catch {
      setNoteError('Could not save this to your notes. Try again.')
    } finally {
      setNoteSaving(false)
    }
  }

  const subtitle = data?.available
    ? `Where ${league?.teamName || 'your team'} wins and loses vs. the league.`
    : (loading ? 'Auditing your roster against the rest of the league…' : undefined)

  const askBarContext = useMemo(() => {
    if (!data?.available) return undefined
    const perfText = (data.topPerformers || []).map((p) => `- ${p.name} (${p.position || '?'}, ${p.team || '?'}) — ${p.why}`).join('\n')
    const tradeText = (data.tradeCandidates || []).map((p) => `- ${p.name} — ${p.reason}`).join('\n')
    const dropText = (data.dropCandidates || []).map((p) => `- ${p.name} — ${p.reason}`).join('\n')
    return `THE USER IS LOOKING AT THEIR TEAM ANALYSER REPORT RIGHT NOW.\n` +
      `Record: ${data.overview?.record || '—'} · Rank: ${data.overview?.rank ? `#${data.overview.rank}` : '—'}\n` +
      `Top performers:\n${perfText || 'None'}\nTrade candidates:\n${tradeText || 'None'}\nDrop candidates:\n${dropText || 'None'}\n` +
      `JAI's thoughts: ${data.thoughts}`
  }, [data])

  const overview = data?.available ? data.overview : null
  const strengths = data?.available ? data.strengths : []
  const weaknesses = data?.available ? data.weaknesses : []

  // Native-only (Guideline 4.2). Headline + a couple of lines + "via JAI" —
  // no logo, no link to pricing.
  const handleShare = () => {
    const lines = [`My ${league?.name || 'team'}, via JAI`]
    if (overview?.record) lines.push(`Record: ${overview.record}`)
    if (overview?.rank) lines.push(`Rank: #${overview.rank}`)
    if (overview?.championshipOdds != null) lines.push(`Championship odds: ${overview.championshipOdds}%`)
    lines.push('via JAI')
    shareText(lines.join('\n'))
  }

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="Team Analyser"
            subtitle={subtitle}
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />

          {/* Overview StatStrip — real record/rank/championship odds, odds cell
              omitted (never a placeholder) when the league is too thin to simulate. */}
          {overview && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
              <StatStrip cells={[
                [overview.record || '—', 'Record'],
                [overview.rank ? `#${overview.rank}` : '—', 'Rank'],
                ...(overview.championshipOdds != null ? [[`${overview.championshipOdds}%`, 'Championship Odds', pctTone(overview.championshipOdds)]] : []),
              ]} />
              {IS_NATIVE_APP && (
                <SecondaryBtn onClick={handleShare} style={{ fontSize: 12, marginTop: 12 }}>Share</SecondaryBtn>
              )}
            </div>
          )}

          {/* asOf + refresh — same shell OpponentScout/ScheduleSurgeon use */}
          {data?.available && (
            <div style={{ marginBottom: 24 }}>
              <UpdatedRow asOf={data.asOf} onRefresh={() => load({ refresh: true })} refreshing={refreshing} label="Refresh analysis" credits="teamAnalyser" />
            </div>
          )}

          {upgrade && <UpgradePrompt tool={upgrade.tool} message={upgrade.message} />}

          {/* Error banner */}
          {error && !upgrade && (
            <Notice tone="error" action="Try again" onAction={() => load()}>{error}</Notice>
          )}

          {/* Inline spinner while the first load is in flight — never a full-page loader */}
          {loading && !error && !upgrade && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
              <JAIMascot size={56} state="thinking" animate={true} />
              <p style={{ fontSize: 13.5, color: V2.faint, margin: 0 }}>Analysing your team…</p>
            </div>
          )}

          {/* Honest pre-draft state — never an empty grid */}
          {!loading && !error && !upgrade && data && !data.available && (
            <V2Card pad="32px 24px" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(10,31,68,0.72)', margin: 0 }}>
                {NO_TEAM_MESSAGE[data.reason] || "JAI can't analyse this team right now."}
              </p>
            </V2Card>
          )}

          {/* Real content */}
          {!loading && !error && data?.available && (
            <>
              {(strengths.length > 0 || weaknesses.length > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {strengths.map((s, i) => <AuditCard key={`s-${i}`} item={s} tone="strength" />)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {weaknesses.map((w, i) => <AuditCard key={`w-${i}`} item={w} tone="weakness" />)}
                  </div>
                </div>
              )}

              {data.topPerformers?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: V2.navy, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Top performers</div>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(data.topPerformers.length, 3)}, 1fr)`, gap: 14 }}>
                    {data.topPerformers.map((p) => <TopPerformerCard key={p.playerId ?? p.name} player={p} sport={sport} />)}
                  </div>
                </div>
              )}

              <CandidateList title="Players who can be traded" players={data.tradeCandidates} leagueId={id} kind="trade" sport={sport} />
              <CandidateList title="Players who can be dropped" players={data.dropCandidates} leagueId={id} kind="drop" sport={sport} />

              {data.thoughts && (
                <DarkPlanCard
                  title="JAI's thoughts"
                  linkLabel={noteSaved ? 'Saved to My Notes ✓' : noteSaving ? 'Saving…' : (noteError || 'Save to My Notes →')}
                  onLink={saveToNotes}
                  style={{ marginBottom: 16 }}
                >
                  <JaiRichText content={data.thoughts} dark style={{ fontSize: 15 }} />
                </DarkPlanCard>
              )}
            </>
          )}
        </div>
      </div>

      <StickyAskBar
        leagueId={id}
        scope="team"
        context={askBarContext}
        placeholder="Ask JAI about your team"
      />
    </AppShell>
  )
}
